/**
 * The one WebSocket for the whole app — every live feature (notifications,
 * task receipts/reactions, sales lead patches, and future Messenger/typing/
 * presence/job-progress) rides this single connection, opened once in
 * AppLayout via useNotificationRealtime. Replaces the old SSE client
 * (notification-stream.ts + sse.ts, both removed) — same reconnect/backoff/
 * token-refresh contract, so the one consumer of this module needed almost
 * no changes.
 *
 * Auth: native WebSocket can't set an Authorization header, so the first
 * client frame after `open` is `{ type: 'auth', token }`; the server
 * upgrades the connection to "authenticated" from there (wsGateway.js) and
 * closes with code 4001 if that frame is missing/invalid/expired. Every
 * server→client frame after that is the versioned envelope:
 *   { v: 1, type: string, payload: unknown, ts: string }
 *
 * Client→server: once authenticated, `sendRealtime(type, payload)` sends
 * `{ v: 1, type, payload }` — for ephemeral app events only (Messenger's
 * typing indicator today). Durable writes always stay REST.
 */
import { API_BASE_URL } from '@/constants/app'
import { tokenStorage } from '@/services/auth/token-storage'

export const WS_PATH = '/realtime'
/** Close code the server sends for "your auth frame/token was no good" —
 * the one code this client treats specially (refresh before retrying). */
const CLOSE_AUTH_FAILED = 4001

export interface RealtimeEnvelope<T = unknown> {
  v: number
  type: string
  payload: T
  ts: string
}

export interface RealtimeStreamEvent {
  type: string
  payload: unknown
}

interface SocketHandlers {
  /** Fires each time the socket (re)connects and is authenticated. `reconnect` is false only on the very first connect. */
  onConnect: (info: { reconnect: boolean }) => void
  onEvent: (event: RealtimeStreamEvent) => void
  onDisconnect?: () => void
}

const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 15_000

// Module-level so `sendRealtime` (called from anywhere — e.g. a typing
// indicator in Messenger) can reach the one connection `openRealtimeSocket`
// manages, without every caller needing a reference to it. Only ever set by
// the single openRealtimeSocket() call AppLayout makes.
let activeSocket: WebSocket | null = null
let activeSocketAuthenticated = false

function backoffDelay(attempt: number): number {
  const cap = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt)
  return cap / 2 + Math.random() * (cap / 2)
}

function wsUrl(): string {
  const url = new URL(API_BASE_URL, window.location.origin)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/+$/, '')}${WS_PATH}`
  url.search = ''
  return url.toString()
}

/**
 * Opens exactly one long-lived WebSocket. Reconnects with backoff on any
 * drop; a server-side auth failure (code 4001) refreshes the access token
 * once before the next attempt instead of retrying the same stale one
 * forever. Returns a close() function to stop reconnecting and disconnect.
 */
export function openRealtimeSocket(handlers: SocketHandlers): () => void {
  let stopped = false
  let attempt = 0
  let hasConnectedOnce = false
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let socket: WebSocket | null = null
  let needsTokenRefresh = false

  function scheduleRetry() {
    if (stopped) return
    handlers.onDisconnect?.()
    const delay = backoffDelay(attempt)
    attempt += 1
    retryTimer = setTimeout(() => void connectOnce(), delay)
  }

  async function connectOnce(): Promise<void> {
    if (stopped) return

    if (needsTokenRefresh) {
      needsTokenRefresh = false
      await tokenStorage.refreshAccessToken()
    }
    const token = tokenStorage.getAccessToken()
    if (!token) {
      scheduleRetry()
      return
    }

    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl())
    } catch {
      scheduleRetry()
      return
    }
    socket = ws
    activeSocket = ws
    activeSocketAuthenticated = false

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token }))
    }

    ws.onmessage = (event) => {
      let envelope: RealtimeEnvelope
      try {
        envelope = JSON.parse(event.data as string) as RealtimeEnvelope
      } catch {
        return // malformed frame — ignore, never tear down the socket for it
      }
      if (envelope.type === 'realtime.connected') {
        attempt = 0
        activeSocketAuthenticated = true
        handlers.onConnect({ reconnect: hasConnectedOnce })
        hasConnectedOnce = true
        return
      }
      if (envelope.type === 'realtime.auth_error') return // close() follows right behind this frame
      handlers.onEvent({ type: envelope.type, payload: envelope.payload })
    }

    ws.onclose = (event) => {
      if (socket === ws) socket = null
      if (activeSocket === ws) {
        activeSocket = null
        activeSocketAuthenticated = false
      }
      // Covers both a rejected auth frame and a token that merely expired
      // mid-connection (server closes with the same code once `exp` passes)
      // — either way, retrying with the same token would just loop.
      if (event.code === CLOSE_AUTH_FAILED) needsTokenRefresh = true
      scheduleRetry()
    }

    ws.onerror = () => {
      // 'close' always follows 'error' for a WebSocket — no separate
      // handling needed, this just avoids an unhandled-error console spam.
    }
  }

  void connectOnce()

  return function close() {
    stopped = true
    if (retryTimer) clearTimeout(retryTimer)
    if (socket) {
      socket.onclose = null
      socket.close(1000, 'Client closing')
      if (activeSocket === socket) {
        activeSocket = null
        activeSocketAuthenticated = false
      }
      socket = null
    }
  }
}

/**
 * Fire-and-forget client→server frame — for ephemeral app events only
 * (typing indicators today; presence/read-ack could follow the same path).
 * Durable writes (send a message, delete, mark seen) stay REST — this is
 * never a substitute for those. A silent no-op while disconnected or still
 * mid-handshake is correct: the receiving end (chat.typing) only cares
 * about "right now," never a queued backlog delivered late.
 */
export function sendRealtime(type: string, payload?: unknown): void {
  if (!activeSocket || !activeSocketAuthenticated || activeSocket.readyState !== activeSocket.OPEN) return
  try {
    activeSocket.send(JSON.stringify({ v: 1, type, payload }))
  } catch {
    // socket gone mid-send — next reconnect will restore normal delivery
  }
}
